import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  TableBody,
  Menu,
  Modal,
  Button,
  LinearProgress,
} from "@mui/material";
import { ReactComponent as ActionIcon } from "../../../../../../vendor/_new/icon/sites/action.svg";
import { ReactComponent as EditIcon } from "../../../../../../vendor/_new/icon/sites/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../vendor/_new/icon/sites/delete.svg";
import { ReactComponent as CloseIcon } from "../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as PlusIcon } from "../../../../../../vendor/_new/icon/sites/plus.svg";
import { tableHeadSx, tableBodySx } from "../../../detection/WAFEvent";
import colors from "../../../../../../utils/colors";
import { GreenSwitch } from "../../../../../common/Greens";
import { generateMenuSx } from "../../../../../../layouts/_new/Normal";
import { formatDate } from "../../../../../../utils/format";
import StickySaveBar from "../../../../../common/StickySaveBar";
import { cloneItems, collectRuleEnabledDiff } from "../../../../../common/stickySaveBarUtils";

import ConfigCustomBlockPageSettingsModal from "./modal/ConfigCustomBlockPageSettings";
import useAuth from "../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../hooks/user/useWAFConfig";
import { CancelButton, containedButtonSx, SaveButton } from "../../../../../common/Button";
import { useNotification } from "../../../../../../contexts/NotificationContext";

const getLabel = (key) => {
  switch (key) {
    case "waf":
      return "WAF";
    case "location":
      return "Location";
    case "interrupt":
      return "Interruption";
    default:
      return "Unknown";
  }
};

const buildBlockPageList = (blockPage) => {
  if (!blockPage) return [];
  return Object.keys(blockPage)
    .map((key) => ({ id: key, description: getLabel(key), ...blockPage[key] }))
    .filter((item) => item.url?.length > 0);
};

const ConfigCustomBlockPageTabContent = ({ siteUid, canConfigureWAFForSite }) => {
  const { configWafSetting, wafConfig, getWAFConfig, setErr } = useWAFConfig();
  const { showSuccess, showError } = useNotification();
  const { isAuthenticated, homeController, wafdashController, websiteController, wafeventController, planController } = useAuth();

  const [isInitialized, setIsInitialized] = useState(true);
  React.useEffect(() => {
    if (isAuthenticated) {
      if (homeController) homeController.abort();
      if (wafdashController) wafdashController.abort();
      if (websiteController) websiteController.abort();
      if (wafeventController) wafeventController.abort();
      if (planController) planController.abort();
      if (siteUid) {
        getWAFConfig(siteUid).then(() => setIsInitialized(false));
      }
    }
    setErr(null);
  }, [
    isAuthenticated,
    getWAFConfig,
    siteUid,
    setErr,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
  ]);

  const [savedBlockPageList, setSavedBlockPageList] = useState([]);
  const [workingBlockPageList, setWorkingBlockPageList] = useState([]);
  const [isSavingToggles, setIsSavingToggles] = useState(false);

  useEffect(() => {
    const nextList = buildBlockPageList(wafConfig?.block_page);
    setSavedBlockPageList(nextList);
    setWorkingBlockPageList(nextList);
  }, [wafConfig?.block_page]);

  const stickyModifiedFields = useMemo(
    () =>
      collectRuleEnabledDiff(savedBlockPageList, workingBlockPageList, (row) => row.description || row.id),
    [savedBlockPageList, workingBlockPageList],
  );
  const stickyModifiedCount = stickyModifiedFields.length;

  const [actionMenu, setActionMenu] = useState({
    anchorEl: null, // the DOM element the menu anchors to
    blockId: null, // index of subrow (or null if main row)
  });
  // open handler (call from the ActionIcon onClick)
  const handleActionOpen = (event, blockId = null) => {
    event.stopPropagation(); // prevent row expand/click side effects
    setActionMenu({ anchorEl: event.currentTarget, blockId });
  };

  // close handler
  const handleActionClose = () => {
    setActionMenu({ anchorEl: null, blockId: null });
  };

  const isActionMenuOpen = Boolean(actionMenu.anchorEl);
  const [isDeleting, setIsDeleting] = useState(false);

  const [blockIdToDelete, setBlockIdToDelete] = useState("");

  const [openBlockPageToDelete, setOpenBlockPageToDelete] = useState(false);
  const handleBlockPageToDeleteOpen = () => setOpenBlockPageToDelete(true);
  const handleBlockPageToDeleteClose = () => setOpenBlockPageToDelete(false);

  const [blockPageToEdit, setBlockPageToEdit] = useState(null);
  const [openBlockPage, setOpenBlockPage] = useState(false);
  const handleBlockPageOpen = () => setOpenBlockPage(true);
  const handleBlockPageClose = () => setOpenBlockPage(false);

  const handleEnableChange = (id, enabled) => {
    if (!canConfigureWAFForSite || isSavingToggles) return;
    setWorkingBlockPageList((prev) =>
      (prev || []).map((item) => (item?.id === id ? { ...item, enabled } : item)),
    );
  };

  const discardToggleChanges = useCallback(() => {
    setWorkingBlockPageList(cloneItems(savedBlockPageList));
  }, [savedBlockPageList]);

  const applyToggleChanges = useCallback(async () => {
    if (!canConfigureWAFForSite || !siteUid || stickyModifiedCount === 0) return;
    const savedById = Object.fromEntries((savedBlockPageList || []).map((row) => [row.id, !!row.enabled]));
    const changed = (workingBlockPageList || []).filter((row) => savedById[row.id] !== !!row.enabled);
    if (changed.length === 0) return;

    const data = {};
    for (const row of changed) {
      data[row.id] = { enabled: !!row.enabled };
    }

    setIsSavingToggles(true);
    try {
      await configWafSetting(siteUid, "set_block_page", data);
      const nextList = cloneItems(workingBlockPageList);
      setSavedBlockPageList(nextList);
      setWorkingBlockPageList(nextList);
      showSuccess(`Saved ${changed.length} custom block page${changed.length === 1 ? "" : "s"}.`);
    } catch (err) {
      showError(err?.message || "Failed to update custom block pages");
    } finally {
      setIsSavingToggles(false);
    }
  }, [
    canConfigureWAFForSite,
    siteUid,
    stickyModifiedCount,
    savedBlockPageList,
    workingBlockPageList,
    configWafSetting,
    showError,
    showSuccess,
  ]);

  const handleDelete = async () => {
    if (!canConfigureWAFForSite) return;
    if (blockIdToDelete) {
      setIsDeleting(true);
      const data = {};
      data[blockIdToDelete] = { url: "" };
      await configWafSetting(siteUid, "set_block_page", data);
      setBlockIdToDelete("");
      setIsDeleting(false);
    }
    handleBlockPageToDeleteClose();
  };

  return (
    <>
      {isInitialized && (
        <Box sx={{ width: "100%", position: "fixed", top: 66 }}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
              "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
              height: 4,
            }}
          />
        </Box>
      )}
      <Stack spacing={6}>
        <Button
          onClick={() => {
            setBlockPageToEdit(null);
            handleBlockPageOpen();
          }}
          disabled={!canConfigureWAFForSite}
          startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
          sx={{ width: 200, height: 32, ...containedButtonSx }}
        >
          Add custom block page
        </Button>
        <Box sx={{ p: 2, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography sx={tableHeadSx}>No</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Description</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>URL</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Status</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={tableHeadSx}>Action</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workingBlockPageList?.map((item, index) => (
                <TableRow key={`block_page_${index}`}>
                  <TableCell>
                    <Typography sx={tableBodySx}>{index + 1}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableBodySx}>{item?.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableBodySx}>{item?.url}</Typography>
                  </TableCell>
                  <TableCell>
                    <GreenSwitch
                      checked={!!item?.enabled}
                      disabled={!canConfigureWAFForSite || isSavingToggles}
                      onChange={(e) => handleEnableChange(item?.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack alignItems={"flex-end"} sx={{ width: "100%" }}>
                      <Box
                        onClick={(e) => canConfigureWAFForSite && handleActionOpen(e, item?.id)}
                        sx={{
                          cursor: canConfigureWAFForSite ? "pointer" : "not-allowed",
                          width: 24,
                          height: 24,
                          opacity: canConfigureWAFForSite ? 1 : 0.4,
                        }}
                      >
                        <ActionIcon style={{ color: colors.neutral[600] }} />
                      </Box>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {workingBlockPageList?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography sx={tableBodySx} textAlign="center">
                      There are no block pages
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <Typography sx={tableBodySx}>Last updated {formatDate(wafConfig?.updated_at)}</Typography>
      </Stack>
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={isActionMenuOpen}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13,
          left: -12,
          "& .MuiList-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            border: `solid 1px ${colors.neutral[200]}`,
            width: 200,
            boxShadow: "none",
            py: 0,
          },
          "& .MuiMenu-paper": {
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { blockId } = actionMenu;
              setBlockPageToEdit(workingBlockPageList?.find((item) => item?.id === blockId));
              handleBlockPageOpen();
              handleActionClose();
            }}
            sx={generateMenuSx(176, 37)}
          >
            <EditIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>Edit</Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            onClick={() => {
              const { blockId } = actionMenu;
              setBlockIdToDelete(blockId);
              handleBlockPageToDeleteOpen();
              handleActionClose();
            }}
            sx={generateMenuSx(176, 37)}
          >
            <DeleteIcon />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>
      <Modal open={openBlockPageToDelete} onClose={handleBlockPageToDeleteClose}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
          }}
        >
          <Stack spacing={3} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Custom Block Page?
            </Typography>
            <Box onClick={handleBlockPageToDeleteClose} sx={{ cursor: "pointer" }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to delete this custom block page?
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={4}>
            <CancelButton onClick={handleBlockPageToDeleteClose} outlined={true} />
            <SaveButton onClick={handleDelete} disabled={isDeleting || !canConfigureWAFForSite} label="Delete" />
          </Stack>
        </Stack>
      </Modal>
      <ConfigCustomBlockPageSettingsModal
        siteUid={siteUid}
        open={openBlockPage}
        handleClose={handleBlockPageClose}
        blockPageToEdit={blockPageToEdit}
        canConfigureWAFForSite={canConfigureWAFForSite}
      />
      <StickySaveBar
        open={canConfigureWAFForSite && stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={discardToggleChanges}
        onApply={applyToggleChanges}
        isApplying={isSavingToggles}
      />
    </>
  );
};
export default ConfigCustomBlockPageTabContent;
